import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, MessageSquare, Eye, EyeOff, ArrowUp, ArrowDown, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  name: "",
  role_de: "",
  role_en: "",
  quote_de: "",
  quote_en: "",
  is_visible: 1,
  sort_order: 0,
};

export default function AdminTestimonialsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/testimonials/all`, { withCredentials: true });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, sort_order: (items.length + 1) * 10 });
    setError("");
    setDialogOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name || "",
      role_de: t.role_de || "",
      role_en: t.role_en || "",
      quote_de: t.quote_de || "",
      quote_en: t.quote_en || "",
      is_visible: t.is_visible,
      sort_order: t.sort_order,
    });
    setError("");
    setDialogOpen(true);
  };

  const save = async () => {
    setError("");
    if (!form.name.trim() || !form.quote_de.trim()) {
      setError("Name und Zitat (DE) sind Pflichtfelder.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/testimonials/${editing.id}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/testimonials`, form, { withCredentials: true });
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Kundenstimme von "${t.name}" wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/testimonials/${t.id}`, { withCredentials: true });
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const toggleVisible = async (t) => {
    try {
      await axios.put(`${API}/testimonials/${t.id}`, { is_visible: t.is_visible ? 0 : 1 }, { withCredentials: true });
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || "Aktion fehlgeschlagen");
    }
  };

  const moveOrder = async (t, dir) => {
    const idx = items.findIndex((x) => x.id === t.id);
    const other = items[idx + dir];
    if (!other) return;
    try {
      await Promise.all([
        axios.put(`${API}/testimonials/${t.id}`, { sort_order: other.sort_order }, { withCredentials: true }),
        axios.put(`${API}/testimonials/${other.id}`, { sort_order: t.sort_order }, { withCredentials: true }),
      ]);
      await load();
    } catch (err) {
      alert("Sortierung fehlgeschlagen");
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#C5993A]" /></div>;
  }

  return (
    <div data-testid="admin-testimonials-tab">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-heading text-xl text-white flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-[#C5993A]" /> Kundenstimmen
        </h3>
        <Button
          data-testid="testimonial-create-btn"
          onClick={openCreate}
          className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0"
        >
          <Plus className="w-4 h-4 mr-2" /> Neu
        </Button>
      </div>

      <p className="text-xs text-[#8899AA] mb-4">
        Kundenstimmen werden auf der Webseite zwischen „Veranstaltungen" und „Kontakt" angezeigt. Auf Desktop als Laufband (Marquee), auf Mobilgeräten als automatisch wechselnde Karte.
      </p>

      {items.length === 0 ? (
        <div className="bg-[#12202f] border border-dashed border-[#3A4F60]/40 rounded-lg p-8 text-center text-[#8899AA]">
          Noch keine Kundenstimmen.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="testimonials-list">
          {items.map((t, i) => (
            <li
              key={t.id}
              data-testid={`testimonial-row-${t.id}`}
              className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-4 flex items-start gap-4"
            >
              <div className="flex flex-col gap-1 flex-shrink-0">
                <button
                  onClick={() => moveOrder(t, -1)}
                  disabled={i === 0}
                  className="w-7 h-7 rounded bg-[#0C1620] border border-[#3A4F60]/40 text-[#C5993A] disabled:opacity-30 hover:bg-[#C5993A]/10 flex items-center justify-center"
                  data-testid={`testimonial-up-${t.id}`}
                  title="Nach oben"
                >
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => moveOrder(t, 1)}
                  disabled={i === items.length - 1}
                  className="w-7 h-7 rounded bg-[#0C1620] border border-[#3A4F60]/40 text-[#C5993A] disabled:opacity-30 hover:bg-[#C5993A]/10 flex items-center justify-center"
                  data-testid={`testimonial-down-${t.id}`}
                  title="Nach unten"
                >
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-white text-sm">{t.name}</p>
                  {t.role_de && <span className="text-[10px] text-[#8899AA]">· {t.role_de}</span>}
                  {!t.is_visible && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">
                      Versteckt
                    </span>
                  )}
                </div>
                <p className="text-[#F0F4F8] text-xs sm:text-sm mt-1.5 line-clamp-2 italic">„{t.quote_de}"</p>
                {t.quote_en && <p className="text-[#8899AA] text-[11px] mt-1 line-clamp-1">EN: „{t.quote_en}"</p>}
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  data-testid={`testimonial-toggle-${t.id}`}
                  onClick={() => toggleVisible(t)}
                  className="w-8 h-8 rounded bg-[#0C1620] border border-[#3A4F60]/40 text-[#C5993A] hover:bg-[#C5993A]/10 flex items-center justify-center"
                  title={t.is_visible ? "Verstecken" : "Anzeigen"}
                >
                  {t.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
                <button
                  data-testid={`testimonial-edit-${t.id}`}
                  onClick={() => openEdit(t)}
                  className="w-8 h-8 rounded bg-[#0C1620] border border-[#3A4F60]/40 text-[#C5993A] hover:bg-[#C5993A]/10 flex items-center justify-center"
                  title="Bearbeiten"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  data-testid={`testimonial-delete-${t.id}`}
                  onClick={() => remove(t)}
                  className="w-8 h-8 rounded bg-[#0C1620] border border-red-400/40 text-red-400 hover:bg-red-400 hover:text-white flex items-center justify-center"
                  title="Löschen"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0C1620] border border-[#3A4F60]/50 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white">
              {editing ? "Kundenstimme bearbeiten" : "Neue Kundenstimme"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-[#8899AA] mb-1.5">Name *</label>
              <Input
                data-testid="testimonial-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-[#12202f] border-[#3A4F60]/50 text-white"
                placeholder='z.B. "Dr. H. Meyer"'
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-[#8899AA] mb-1.5">Rolle/Position (DE)</label>
                <Input
                  data-testid="testimonial-role-de-input"
                  value={form.role_de}
                  onChange={(e) => setForm({ ...form, role_de: e.target.value })}
                  className="bg-[#12202f] border-[#3A4F60]/50 text-white"
                  placeholder='z.B. "Unternehmer, Bayern"'
                />
              </div>
              <div>
                <label className="block text-xs text-[#8899AA] mb-1.5">Rolle/Position (EN)</label>
                <Input
                  data-testid="testimonial-role-en-input"
                  value={form.role_en}
                  onChange={(e) => setForm({ ...form, role_en: e.target.value })}
                  className="bg-[#12202f] border-[#3A4F60]/50 text-white"
                  placeholder='e.g. "Entrepreneur, Bavaria"'
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-[#8899AA] mb-1.5">Zitat (DE) *</label>
              <Textarea
                data-testid="testimonial-quote-de-input"
                rows={4}
                value={form.quote_de}
                onChange={(e) => setForm({ ...form, quote_de: e.target.value })}
                className="bg-[#12202f] border-[#3A4F60]/50 text-white"
              />
            </div>
            <div>
              <label className="block text-xs text-[#8899AA] mb-1.5">Zitat (EN)</label>
              <Textarea
                data-testid="testimonial-quote-en-input"
                rows={4}
                value={form.quote_en}
                onChange={(e) => setForm({ ...form, quote_en: e.target.value })}
                className="bg-[#12202f] border-[#3A4F60]/50 text-white"
              />
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-[#F0F4F8]">
                <input
                  data-testid="testimonial-visible-checkbox"
                  type="checkbox"
                  checked={!!form.is_visible}
                  onChange={(e) => setForm({ ...form, is_visible: e.target.checked ? 1 : 0 })}
                  className="w-4 h-4 accent-[#C5993A]"
                />
                Sichtbar auf der Webseite
              </label>
            </div>

            {error && <p className="text-red-400 text-sm" data-testid="testimonial-error">{error}</p>}
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDialogOpen(false)}
              className="text-[#8899AA] hover:text-white"
            >
              Abbrechen
            </Button>
            <Button
              data-testid="testimonial-save-btn"
              onClick={save}
              disabled={saving}
              className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0"
            >
              {saving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Speichert…</>) : "Speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
